import { Reference } from '@approvalmax/types';
import { DateTimeEditor, DropdownEditor, Field, TextEditor } from '@approvalmax/ui';
import lowerFirst from 'lodash/lowerFirst';
import { dataProviders } from 'modules/common';
import { domain } from 'modules/data';
import { PlainDataProvider } from 'modules/data-providers';
import { memo } from 'react';

import { PLACEHOLDER_FIELD } from '../../RequestSearchPopup.constants';
import { DateFieldRow, DateRow, WrapFieldRow } from '../../RequestSearchPopup.styles';
import { messages } from './XeroSearchFields.messages';
import { XeroSearchFieldsProps } from './XeroSearchFields.types';

export const XeroSearchFields = memo<XeroSearchFieldsProps>((props) => {
    const { company, integrationCode, integrationEnabled, params, updateParameter, disabled } = props;

    const isContact = integrationCode === domain.IntegrationCode.XeroContact;
    const isBillBatchPayment = integrationCode === domain.IntegrationCode.XeroBillBatchPayment;
    const isAirwallexBatchPayment = integrationCode === domain.IntegrationCode.XeroAirwallexBatchPayment;
    const isManualJournal = integrationCode === domain.IntegrationCode.XeroManualJournal;
    const isCustomerField =
        integrationCode &&
        [
            domain.IntegrationCode.XeroInvoice,
            domain.IntegrationCode.XeroQuote,
            domain.IntegrationCode.XeroCreditNotesReceivable,
        ].includes(integrationCode);

    return (
        <>
            <WrapFieldRow>
                <Field title={messages.xeroRequestorFieldTitle}>
                    <PlainDataProvider items={company?.allMembers || []} filterAttribute='displayName'>
                        <DropdownEditor
                            displayAttribute='displayName'
                            placeholder={messages.xeroRequestorPlaceholder}
                            value={params.requestor}
                            onChange={updateParameter('requestor')}
                            disabled={disabled}
                        />
                    </PlainDataProvider>
                </Field>

                {isManualJournal && (
                    <Field title={messages.narrationFieldTitle}>
                        <TextEditor
                            placeholder={messages.narrationFieldPlaceholder}
                            value={params.xeroNarration}
                            onChange={updateParameter('xeroNarration')}
                            disabled={disabled}
                        />
                    </Field>
                )}

                {!isManualJournal && (
                    <Field title={messages.xeroContactFieldTitle}>
                        <dataProviders.FieldDataProvider
                            key={params.company?.id}
                            integrationCode={integrationCode}
                            field={
                                params.company?.fields.find((f) =>
                                    isCustomerField
                                        ? f.systemPurpose === domain.FieldSystemPurpose.XeroCustomer
                                        : f.systemPurpose === domain.FieldSystemPurpose.XeroSupplier
                                ) || PLACEHOLDER_FIELD
                            }
                        >
                            <DropdownEditor
                                placeholder={messages.xeroContactPlaceholder}
                                value={params.xeroContact}
                                onChange={updateParameter('xeroContact')}
                                disabled={disabled || !integrationEnabled || isContact || isAirwallexBatchPayment}
                            />
                        </dataProviders.FieldDataProvider>
                    </Field>
                )}

                <Field title={messages.xeroAccountFieldTitle}>
                    <dataProviders.FieldDataProvider
                        key={params.company?.id}
                        integrationCode={integrationCode}
                        field={
                            params.company?.fields.find(
                                (f) => f.systemPurpose === domain.FieldSystemPurpose.XeroAccount
                            ) || PLACEHOLDER_FIELD
                        }
                    >
                        <DropdownEditor
                            placeholder={messages.xeroAccountPlaceholder}
                            value={params.xeroAccount}
                            onChange={updateParameter('xeroAccount')}
                            disabled={
                                disabled ||
                                !integrationEnabled ||
                                isContact ||
                                isBillBatchPayment ||
                                isAirwallexBatchPayment
                            }
                        />
                    </dataProviders.FieldDataProvider>
                </Field>

                {!isManualJournal && (
                    <Field title={messages.xeroItemFieldTitle}>
                        <dataProviders.FieldDataProvider
                            key={params.company?.id}
                            integrationCode={integrationCode}
                            field={
                                params.company?.fields.find(
                                    (f) => f.systemPurpose === domain.FieldSystemPurpose.XeroItem
                                ) || PLACEHOLDER_FIELD
                            }
                        >
                            <DropdownEditor
                                placeholder={messages.xeroItemPlaceholder}
                                value={params.xeroItem}
                                onChange={updateParameter('xeroItem')}
                                disabled={
                                    disabled ||
                                    !integrationEnabled ||
                                    isContact ||
                                    isBillBatchPayment ||
                                    isAirwallexBatchPayment
                                }
                            />
                        </dataProviders.FieldDataProvider>
                    </Field>
                )}

                {params.company?.fields
                    .filter((f) => f.systemPurpose === domain.FieldSystemPurpose.XeroTracking)
                    .map((f) => {
                        const category = params.xeroTrackingCategories.find((t) => t.category.id === f.id);

                        return (
                            <Field title={f.name} key={f.id}>
                                <dataProviders.FieldDataProvider field={f} integrationCode={integrationCode}>
                                    <DropdownEditor
                                        placeholder={messages.xeroTrackingPlaceholder({
                                            name: lowerFirst(f.name),
                                        })}
                                        disabled={
                                            disabled ||
                                            !integrationEnabled ||
                                            isContact ||
                                            isBillBatchPayment ||
                                            isAirwallexBatchPayment
                                        }
                                        value={category ? category.value : null}
                                        onChange={(value: Reference) =>
                                            updateParameter('xeroTrackingCategories')(
                                                value
                                                    ? params.xeroTrackingCategories
                                                          .filter((c) => c.category.id !== f.id)
                                                          .concat([
                                                              {
                                                                  category: {
                                                                      id: f.id,
                                                                      text: f.name,
                                                                  },
                                                                  value,
                                                              },
                                                          ])
                                                    : params.xeroTrackingCategories.filter(
                                                          (c) => c.category.id !== f.id
                                                      )
                                            )
                                        }
                                    />
                                </dataProviders.FieldDataProvider>
                            </Field>
                        );
                    })}

                {!isManualJournal && (
                    <Field title={messages.xeroDocumentNumberFieldTitle}>
                        <TextEditor
                            placeholder={messages.xeroDocumentNumberPlaceholder}
                            value={params.xeroDocumentNumber}
                            onChange={updateParameter('xeroDocumentNumber')}
                            disabled={
                                disabled ||
                                isContact ||
                                (params.template?.integrationCode &&
                                    [
                                        domain.IntegrationCode.XeroBillBatchPayment,
                                        domain.IntegrationCode.XeroBill,
                                        domain.IntegrationCode.XeroCreditNotesPayable,
                                    ].includes(params.template.integrationCode))
                            }
                        />
                    </Field>
                )}

                {!isManualJournal && (
                    <Field title={messages.xeroDocumentReferenceFieldTitle}>
                        <TextEditor
                            placeholder={messages.xeroDocumentReferencePlaceholder}
                            disabled={disabled || isContact || isBillBatchPayment || isAirwallexBatchPayment}
                            value={params.xeroDocumentReference}
                            onChange={updateParameter('xeroDocumentReference')}
                        />
                    </Field>
                )}
            </WrapFieldRow>

            <DateRow>
                <Field title={messages.dateFieldTitle}>
                    <DateFieldRow>
                        <div>{messages.dateFromLabel}</div>

                        <DateTimeEditor
                            placeholder={messages.dateGreaterEqualsPlaceholder}
                            disabled={disabled || isContact}
                            value={params.documentDateGreaterEquals}
                            onChange={updateParameter('documentDateGreaterEquals')}
                        />

                        <div>{messages.dateToLabel}</div>

                        <DateTimeEditor
                            placeholder={messages.dateLessEqualsPlaceholder}
                            disabled={disabled || isContact}
                            value={params.documentDateLessEquals}
                            onChange={updateParameter('documentDateLessEquals')}
                        />
                    </DateFieldRow>
                </Field>
            </DateRow>

            {!isManualJournal && (
                <DateRow>
                    <Field title={messages.airwallexScheduledPaymentDateFieldTitle}>
                        <DateFieldRow>
                            <div>{messages.dateFromLabel}</div>

                            <DateTimeEditor
                                placeholder={messages.dateGreaterEqualsPlaceholder}
                                disabled={disabled || !isAirwallexBatchPayment}
                                value={params.airwallexScheduledPaymentDateGreaterEquals}
                                onChange={updateParameter('airwallexScheduledPaymentDateGreaterEquals')}
                            />

                            <div>{messages.dateToLabel}</div>

                            <DateTimeEditor
                                placeholder={messages.dateLessEqualsPlaceholder}
                                disabled={disabled || !isAirwallexBatchPayment}
                                value={params.airwallexScheduledPaymentDateLessEquals}
                                onChange={updateParameter('airwallexScheduledPaymentDateLessEquals')}
                            />
                        </DateFieldRow>
                    </Field>
                </DateRow>
            )}
        </>
    );
});

export default XeroSearchFields;
